import { type Deck, flattenDeck } from "@hali-shares/deck-schema";
import { Component, type ReactNode, useEffect, useMemo, useState } from "react";
import { STAGE_H, STAGE_W } from "../camera/ScaledStage";
import { TopBar } from "../routes/present/TopBar";
import { PresenterContext, type PresenterCtx } from "./context";
import type { FlowLayout } from "./flow/layout";
import { layoutDeck } from "./flow/layouts";
import { buildModel } from "./model";
import { SlideView } from "./SlideView";
import "../routes/present/present.css";

const NOOP = () => {};
const IDLE_CLOCK = { started: false, running: false, times: {}, toggle: NOOP, reset: NOOP };

/**
 * One slide exactly as the presenter draws it (same components, same 1280×720 stage), scaled to `width`.
 * For editing: it takes a draft deck, never touches the server, and can't be clicked.
 */
export function SlidePreview({
  deck,
  slideIndex,
  stop = 0,
  width,
  revealed = false,
}: {
  deck: Deck;
  slideIndex: number;
  stop?: number;
  width: number;
  /** Show poll/quiz results (correct answer, explanation) as on reveal. */
  revealed?: boolean;
}) {
  const model = useMemo(() => buildModel(deck), [deck]);
  const [layouts, setLayouts] = useState<Map<string, FlowLayout>>(new Map());

  // Flow layouts are async (ELK). Debounced while typing; the previous layout stays up meanwhile.
  useEffect(() => {
    let live = true;
    const t = window.setTimeout(() => {
      layoutDeck(deck).then(
        (l) => live && setLayouts(l),
        () => {},
      );
    }, 150);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [deck]);

  const slides = model.slides;
  const index = Math.min(Math.max(0, slideIndex), slides.length - 1);
  const slide = slides[index];
  const ctx = useMemo<PresenterCtx>(
    () => ({
      model,
      layouts,
      joinUrl: `${window.location.origin}/j`,
      code: null,
      position: { slideIndex: index, stop },
      goto: NOOP,
      gotoReferences: NOOP,
      poll: () => ({ locked: false, revealed }),
      togglePoll: NOOP,
      tally: () => undefined,
      live: null,
      leaderboard: null,
      clock: IDLE_CLOCK,
      theme: "light",
      setTheme: NOOP,
      cycleTheme: NOOP,
      openNotes: NOOP,
      preview: true,
    }),
    [model, layouts, index, stop, revealed],
  );
  if (!slide) return null;
  const scale = width / STAGE_W;

  return (
    <div
      className="slide-preview"
      style={{ width, height: STAGE_H * scale }}
      aria-hidden="true"
      data-testid="slide-preview"
    >
      <div
        className="slide-preview-stage"
        style={{ width: STAGE_W, height: STAGE_H, transform: `scale(${scale})` }}
        // inert: nothing inside can be focused or clicked (React 18's types don't know the attribute yet)
        ref={(el) => el?.setAttribute("inert", "")}
      >
        <PreviewBoundary resetKey={deck}>
          <PresenterContext.Provider value={ctx}>
            {slide.kind !== "title" && <TopBar />}
            <SlideView key={index} slide={slide} slideIndex={index} stop={stop} />
          </PresenterContext.Provider>
        </PreviewBoundary>
      </div>
    </div>
  );
}

/** Slide index of a block in a (draft) deck, or -1. */
export function slideIndexOf(deck: Deck, blockId: string): number {
  return flattenDeck(deck).findIndex((s) => s.kind === "block" && s.blockId === blockId);
}

/** A half-finished edit must never take the editor down with it. */
class PreviewBoundary extends Component<{ resetKey: unknown; children: ReactNode }, { failed: boolean; key: unknown }> {
  override state = { failed: false, key: this.props.resetKey };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  static getDerivedStateFromProps(props: { resetKey: unknown }, state: { failed: boolean; key: unknown }) {
    return props.resetKey !== state.key ? { failed: false, key: props.resetKey } : null;
  }
  override render() {
    return this.state.failed ? (
      <div className="slide-preview-error">Preview catches up once this is complete.</div>
    ) : (
      this.props.children
    );
  }
}
